/**
 * Anonymous Firebase Authentication
 * 
 * Provides seamless authentication without requiring user sign-in.
 * Each device/browser gets a unique anonymous user ID that persists.
 * This allows cross-device sync via Firebase while maintaining privacy.
 */

import { getAuth, signInAnonymously, onAuthStateChanged, User } from 'firebase/auth';
import { app } from './firebase';

const auth = getAuth(app);

/**
 * Get or create anonymous user
 * Returns a promise that resolves with the user ID
 */
export async function getAnonymousUser(): Promise<string> {
  return new Promise((resolve, reject) => {
    // Check if already signed in
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      unsubscribe(); // Unsubscribe after first check
      
      if (user) {
        // Already signed in
        console.log(' Anonymous user already signed in:', user.uid);
        resolve(user.uid);
      } else {
        // Sign in anonymously
        try {
          const result = await signInAnonymously(auth);
          console.log(' New anonymous user created:', result.user.uid);
          resolve(result.user.uid);
        } catch (error) {
          console.error(' Anonymous sign-in failed:', error);
          reject(error);
        }
      }
    });
  });
}

/**
 * Get current user ID synchronously (returns null if not signed in)
 */
export function getCurrentUserId(): string | null {
  return auth.currentUser?.uid || null;
}

/**
 * Subscribe to auth state changes
 */
export function onAuthChanged(callback: (userId: string | null) => void): () => void {
  return onAuthStateChanged(auth, (user) => {
    callback(user?.uid || null);
  });
}

/**
 * Ensure user is authenticated before performing operation
 */
export async function withAuth<T>(operation: (userId: string) => Promise<T>): Promise<T> {
  const userId = await getAnonymousUser();
  return operation(userId);
}

